/**
 * Component Renderer Registry Implementation
 *
 * Provides a registry system for the UnifiedLeadTracker to hook into
 * the component rendering system without tight coupling.
 */

import type {
  ComponentRendererRegistry,
  FormCompletionData,
  HookResponse
} from './form-hook-types'

/**
 * Handler function type for form completion events
 */
type FormCompletionHandler = (data: FormCompletionData) => Promise<void>

/**
 * Global component renderer registry singleton
 *
 * This registry allows the UnifiedLeadTracker to register form completion
 * handlers that will trigger the component rendering system.
 */
class ComponentRendererRegistryImpl implements ComponentRendererRegistry {
  private handlers: FormCompletionHandler[] = []
  private debugMode = false

  /**
   * Register a form completion handler
   */
  onFormCompletion(handler: FormCompletionHandler): void {
    this.handlers.push(handler)

    if (this.debugMode) {
      console.log('🎯 Component renderer handler registered. Total handlers:', this.handlers.length)
    }
  }

  /**
   * Unregister all handlers (for cleanup)
   */
  clearHandlers(): void {
    const prevCount = this.handlers.length
    this.handlers = []

    if (this.debugMode) {
      console.log('🧹 Cleared', prevCount, 'component renderer handlers')
    }
  }

  /**
   * Check if any handlers are registered
   */
  hasHandlers(): boolean {
    return this.handlers.length > 0
  }

  /**
   * Enable/disable debug mode
   */
  setDebugMode(enabled: boolean): void {
    this.debugMode = enabled
  }

  /**
   * Trigger all registered handlers with form completion data
   * This is called by the UnifiedLeadTracker when forms are submitted
   */
  async triggerFormCompletion(data: FormCompletionData): Promise<HookResponse[]> {
    if (this.handlers.length === 0) {
      if (this.debugMode) {
        console.log('🎯 No component renderer handlers registered, skipping')
      }
      return []
    }

    const startTime = Date.now()
    const responses: HookResponse[] = []

    if (this.debugMode) {
      console.log('🎯 Triggering', this.handlers.length, 'component renderer handlers with data:', data)
    }

    for (const handler of this.handlers) {
      const handlerStartTime = Date.now()
      try {
        await handler(data)
        const processingTime = Date.now() - handlerStartTime

        responses.push({
          success: true,
          processingTime,
        })

        if (this.debugMode) {
          console.log(`✅ Component renderer handler completed in ${processingTime}ms`)
        }
      } catch (error) {
        const processingTime = Date.now() - handlerStartTime
        const errorMessage = error instanceof Error ? error.message : 'Unknown error'

        responses.push({
          success: false,
          error: errorMessage,
          processingTime,
        })

        if (this.debugMode) {
          console.error(`❌ Component renderer handler failed after ${processingTime}ms:`, error)
        }
      }
    }

    const totalTime = Date.now() - startTime
    if (this.debugMode) {
      console.log(`🎯 All component renderer handlers completed in ${totalTime}ms`)
    }

    return responses
  }
}

/**
 * Global singleton instance
 * This is the main interface that both UnifiedLeadTracker and ComponentRenderer use
 */
export const componentRendererRegistry = new ComponentRendererRegistryImpl()

/**
 * Initialize the registry with debug mode setting
 */
export function initializeComponentRendererRegistry(debugMode = false): void {
  componentRendererRegistry.setDebugMode(debugMode)

  if (debugMode) {
    console.log('🎯 Component renderer registry initialized with debug mode enabled')
  }
}